<template>
    <footer ref="footer" :class="['footer', { ['footer--dark']: isDarkTheme }]" v-if="!isWebview">
      <div class="footer__row">
        <div class="footer__left-container footer__links">
          <ul
            v-for="column in columns"
            class="footer__column footer__list"
            role="list"
            :key="column.name"
          >
            <NavigationItem
              v-for="route in getColumnItems(column.items)"
              class="footer__item"
              :key="route.link.value.text"
            >
              <ShLink
                v-if="route.link.value.href"
                :to="route.link.value.href"
                :target="route.link.value.target"
              >
                {{ route.link.value.text }}
              </ShLink>
            </NavigationItem>
          </ul>
        </div>
        <Subscribe v-if="!currentUser" class="footer__subscribe" />
      </div>
      <div class="footer__row">
        <div class="footer__left-container">
          <div class="footer__column footer__logo">
            <logo size="xsmall" :invert="isDarkTheme" :hasIcon="true" />
          </div>
          <div class="footer__column footer__lang">
            <language-selector />
          </div>
        </div>
        <div class="footer__column footer__year">{{ footer.currentYear.value }}</div>
      </div>
    </footer>
</template>

<script>
import { mapGetters, createNamespacedHelpers } from 'vuex'
import { NavigationItem, ShIcon } from '@sohohouse/design-system'

import LanguageSelector from '~/components/language-selector'
import Subscribe from '~/components/subscribe'
import ShLink from '~/components/sh-link'
import Logo from '~/components/logo'

const { mapState: mapEnvState, mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')

export default {
  name: 'ShFooter',

  components: {
    NavigationItem,
    ShIcon,
    LanguageSelector,
    Subscribe,
    ShLink,
    Logo
  },

  provide () {
    return { dark: this.isDarkTheme }
  },

  computed: {
    ...mapGetters(['currentUser']),
    ...mapEnvState(['footer']),
    ...mapEnvGetters(['isWebview', 'isDarkTheme']),
    ...mapGetters(['currentUser']),

    columns () {
      return this.footer.columns || []
    }
  },

  methods: {
    focus () {
      this.$refs.footer.focus()
    },

    getColumnItems (items) {
      if (!items) return []
      if (this.$device.isMobileOrTablet) return items

      return items.filter(item => !item.mobileAndTabletOnly)
    }
  }
}
</script>


<style lang="scss" scoped>
$column-width-desktop: 20%;
$column-width-tablet: 25%;

.footer {
  @include font('040');

  width: 100%;
  padding: $space-050;

  h2 {
    @include font('040');
  }

  &__row {
    display: flex;
    flex-wrap: wrap;

    &:not(:last-child) {
      margin-bottom: $space-070;
    }

    @include tablet {
      flex-wrap: nowrap;
    }
  }
  &__column {
    display: flex;
    flex-direction: column;

    @include tablet {
      width: $column-width-tablet;
    }
    @include desktop {
      width: $column-width-desktop;
    }
  }
  &__left-container {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
  }
  &__links {
    display: block;
    columns: 2;

    @include tablet {
      display: flex;
      flex-direction: row;
    }
  }
  &__list {
    margin: 0;
    padding: 0;
    display: block;

    @include tablet {
      display: flex;
    }
  }
  &__item {
    white-space: inherit;

    a {
      @include font('050');

      @include desktop {
        @include font('060');
      }
    }
  }

  &__subscribe {
    width: 100%;
    margin-top: $space-070;

    @include tablet {
      margin-top: 0;
      margin-left: auto;
      width: 30%;
      flex-shrink: 0;
    }
  }

  &__logo {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    order: 2;
    margin-bottom: $space-030;

    @include tablet {
      order: 1;
      width: $column-width-tablet;
      margin-bottom: unset;
    }
    @include desktop {
      width: $column-width-desktop;
    }
  }

  &__lang {
    order: 1;
    width: 100%;
    margin-bottom: $space-070;

    @include tablet {
      order: 2;
      width: $column-width-tablet;
      margin-bottom: 0;
    }
    @include desktop {
      width: $column-width-desktop;
    }
  }

  &__year {
    width: 100%;
    order: 3;

    @include tablet {
      width: 30%;
      flex-shrink: 0;
      text-align: right;
      margin-left: auto;
      justify-content: flex-end;
    }
  }

  &--dark {
    background-color: $color-black;
    color: $color-white;

    h2, .footer__item > a {
      color: $color-white;
    }
  }
}
</style>
